import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  FILE_ACCEPT_GROUPS,
  newChoiceId,
  type Choice,
  type FieldType,
  type FormField,
} from "@koaform/form-schema";
import { RatingIcon, RATING_SHAPES } from "@koaform/ui";
import { Bars2Icon, PlusIcon, XMarkIcon } from "@heroicons/react/24/outline";

import { convertFieldType, FIELD_GROUPS } from "./fieldDefaults.js";

// 10px under a label, 20px under the control it labels: the gap that groups
// has to be clearly smaller than the gap that separates, or the panel reads
// as one undifferentiated column of boxes.
// The label text is its own block INSIDE the label element, so this margin
// sits between a label and its control. Putting it on the wrapping <label>
// instead — the obvious markup — lands the gap below the control and leaves
// the two flush, which is how the panel ended up with two different spacings.
const labelCls = "mb-2 block text-[12.5px] font-semibold text-body";
/** Spacing between one labelled control and the next. */
const fieldCls = "mb-5 block";
const inputCls =
  "block w-full appearance-none rounded-[10px] border border-line-input px-3.5 py-[11px] text-sm text-ink placeholder-faint focus:border-primary-600 focus:outline-none";

type Props = {
  field: FormField;
  onChange: (field: FormField) => void;
};

const groupCls =
  "mt-6 mb-3 border-t border-line pt-5 text-[11px] font-semibold tracking-[0.06em] text-faint uppercase";

/** "" → undefined, so clearing a numeric input removes the rule entirely. */
const numberOrUndefined = (raw: string): number | undefined =>
  raw === "" ? undefined : Number(raw);

/**
 * An optional setting: a switch, and its input only once the switch is on.
 *
 * This is the Tally shape, and it is the right one — an always-visible empty
 * "Minimum" box cannot say whether it means "no minimum" or "a minimum you
 * have not typed yet". Turning the switch off clears the value, so the
 * document never carries a constraint the panel is not displaying.
 *
 * `locked` is for settings that always have a value (a maximum length always
 * exists): the switch is shown as on and non-interactive, so the row reads
 * consistently with its neighbours rather than being a different shape.
 */
const ToggleSetting = ({
  title,
  hint,
  on,
  locked = false,
  onFlip,
  children,
}: {
  title: string;
  hint?: string;
  on: boolean;
  locked?: boolean;
  onFlip?: (on: boolean) => void;
  children: React.ReactNode;
}) => (
  <div className="mb-2">
    <button
      type="button"
      role="switch"
      aria-checked={on}
      disabled={locked}
      onClick={() => onFlip?.(!on)}
      className={`flex w-full items-center justify-between gap-3 rounded-[10px] px-1 py-2 text-left ${
        locked ? "cursor-default" : "cursor-pointer hover:bg-paper"
      }`}
    >
      <span className="min-w-0">
        <span className="block text-[13.5px] font-semibold text-ink">
          {title}
        </span>
        {hint ? (
          <span className="mt-0.5 block text-[11.5px] leading-[1.45] text-faint">
            {hint}
          </span>
        ) : null}
      </span>
      <span
        className={`flex h-[22px] w-[38px] flex-none rounded-full p-[3px] transition-colors ${
          on ? "justify-end bg-primary-600" : "justify-start bg-line-bold"
        } ${locked ? "opacity-45" : ""}`}
      >
        <span className="h-4 w-4 rounded-full bg-white" />
      </span>
    </button>
    {on ? <div className="mt-1 mb-3 pl-1">{children}</div> : null}
  </div>
);

/** One draggable choice row: grip, label input, remove. */
const ChoiceRow = ({
  choice,
  index,
  removable,
  onLabel,
  onRemove,
  onEnter,
}: {
  choice: Choice;
  index: number;
  removable: boolean;
  onLabel: (label: string) => void;
  onRemove: () => void;
  /** Enter in the label input — add the next choice, like every list editor. */
  onEnter: () => void;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = // prettier-ignore
    useSortable({ id: choice.id });

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`flex items-center gap-1 ${isDragging ? "opacity-60" : ""}`}
    >
      <button
        type="button"
        aria-label={`Reorder choice ${index + 1}`}
        {...attributes}
        {...listeners}
        className="mb-4 flex h-[38px] w-[22px] flex-none cursor-grab items-center justify-center rounded-lg text-faint hover:text-ink active:cursor-grabbing"
      >
        <Bars2Icon className="size-3.5" />
      </button>
      <input
        className={inputCls}
        value={choice.label}
        onChange={(e) => onLabel(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && onEnter()}
      />
      <button
        type="button"
        aria-label={`Remove choice ${index + 1}`}
        disabled={!removable}
        onClick={onRemove}
        className="mb-4 flex h-[38px] w-[30px] flex-none items-center justify-center rounded-lg border border-line-input bg-surface text-faint hover:border-danger hover:text-danger disabled:opacity-30"
      >
        <XMarkIcon className="size-4" />
      </button>
    </li>
  );
};

export const FieldSettings = ({ field, onChange }: Props) => {
  const patch = (changes: Partial<FormField>) =>
    onChange({ ...field, ...changes } as FormField);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );

  const reorderChoices = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id || !("choices" in field)) return;
    const from = field.choices.findIndex((c) => c.id === active.id);
    const to = field.choices.findIndex((c) => c.id === over.id);
    if (from === -1 || to === -1) return;
    patch({ choices: arrayMove(field.choices, from, to) } as Partial<FormField>); // prettier-ignore
  };

  const addChoice = () => {
    if (!("choices" in field)) return;
    patch({
      // A fresh id every time. Reusing a removed choice's id would make old
      // responses silently point at a different answer.
      choices: [
        ...field.choices,
        { id: newChoiceId(), label: `Option ${field.choices.length + 1}` },
      ],
    } as Partial<FormField>);
  };

  return (
    <div className="px-[18px] py-5">
      {/* The mobile sheet already titles itself BLOCK SETTINGS. */}
      <h2 className="mb-[18px] hidden font-mono text-[10px] tracking-[0.1em] text-faint lg:block">
        BLOCK SETTINGS
      </h2>

      <label className={fieldCls}>
        <span className={labelCls}>Type</span>
        <select
          className={inputCls}
          value={field.type}
          onChange={(e) =>
            onChange(convertFieldType(field, e.target.value as FieldType))
          }
        >
          {FIELD_GROUPS.map((group) => (
            <optgroup key={group.group} label={group.group}>
              {group.items.map((item) => (
                <option key={item.type} value={item.type}>
                  {item.label}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </label>

      {"text" in field ? (
        <label className={fieldCls}>
          <span className={labelCls}>Text</span>
          <textarea
            className={inputCls}
            rows={3}
            value={field.text}
            onChange={(e) => patch({ text: e.target.value } as Partial<FormField>)} // prettier-ignore
          />
        </label>
      ) : null}

      {"label" in field ? (
        <>
          <label className={fieldCls}>
            <span className={labelCls}>Question</span>
            <input
              className={inputCls}
              value={field.label}
              onChange={(e) => patch({ label: e.target.value } as Partial<FormField>)} // prettier-ignore
            />
          </label>

          <label className={fieldCls}>
            <span className={labelCls}>Description</span>
            <input
              className={inputCls}
              placeholder="Optional help text"
              value={field.description ?? ""}
              onChange={(e) =>
                patch({
                  description: e.target.value || undefined,
                } as Partial<FormField>)
              }
            />
          </label>

          <button
            type="button"
            role="switch"
            aria-checked={field.required}
            onClick={() => patch({ required: !field.required } as Partial<FormField>)} // prettier-ignore
            className="mb-4 flex w-full cursor-pointer items-center justify-between rounded-[10px] border border-line-input px-[13px] py-3 text-left hover:border-primary-600"
          >
            <span>
              <span className="block text-sm font-semibold text-ink">
                Required
              </span>
              <span className="mt-0.5 block text-xs text-faint">
                Must be answered to submit
              </span>
            </span>
            <span
              className={`flex h-[23px] w-10 flex-none rounded-full p-[3px] transition-colors ${
                field.required
                  ? "justify-end bg-primary-600"
                  : "justify-start bg-line-bold"
              }`}
            >
              <span className="h-[17px] w-[17px] rounded-full bg-white" />
            </span>
          </button>
        </>
      ) : null}

      {"choices" in field ? (
        <div>
          <span className={`${labelCls} mt-1`}>Choices</span>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis]}
            onDragEnd={reorderChoices}
          >
            <SortableContext
              items={field.choices.map((c) => c.id)}
              strategy={verticalListSortingStrategy}
            >
              <ul className="mt-1 flex flex-col gap-1.5">
                {field.choices.map((choice, i) => (
                  <ChoiceRow
                    key={choice.id}
                    choice={choice}
                    index={i}
                    removable={field.choices.length > 1}
                    onLabel={(label) =>
                      patch({
                        choices: field.choices.map((c) =>
                          c.id === choice.id ? { ...c, label } : c,
                        ),
                      } as Partial<FormField>)
                    }
                    onRemove={() =>
                      patch({
                        choices: field.choices.filter((c) => c.id !== choice.id), // prettier-ignore
                      } as Partial<FormField>)
                    }
                    onEnter={addChoice}
                  />
                ))}
              </ul>
            </SortableContext>
          </DndContext>
          <button
            type="button"
            onClick={addChoice}
            className="mt-2 flex w-full items-center justify-center gap-1 rounded-[9px] border border-dashed border-line-bold bg-subtle py-2.5 text-[13px] font-semibold text-primary-600 hover:border-primary-600"
          >
            <PlusIcon className="size-4" /> Add choice
          </button>

          {"allowOther" in field ? (
            <label className="mt-3 flex cursor-pointer items-center gap-2 text-sm text-body">
              <input
                type="checkbox"
                className="rounded border-line-bold text-primary-600"
                checked={field.allowOther}
                onChange={(e) => patch({ allowOther: e.target.checked } as Partial<FormField>)} // prettier-ignore
              />
              Allow an “Other” answer
            </label>
          ) : null}

          {"randomize" in field ? (
            <label className="mt-2.5 flex cursor-pointer items-center gap-2 text-sm text-body">
              <input
                type="checkbox"
                className="rounded border-line-bold text-primary-600"
                checked={field.randomize}
                onChange={(e) => patch({ randomize: e.target.checked } as Partial<FormField>)} // prettier-ignore
              />
              Show choices in a random order
            </label>
          ) : null}
        </div>
      ) : null}

      {field.type === "rating" ? (
        <>
          <span className={labelCls}>Scale</span>
          <div className="mb-5 grid grid-cols-3 gap-1.5">
            {RATING_SHAPES.map((option) => {
              const active = field.shape === option.shape;
              return (
                <button
                  key={option.shape}
                  type="button"
                  onClick={() => patch({ shape: option.shape } as Partial<FormField>)} // prettier-ignore
                  className={`rounded-[10px] border px-1 py-2.5 ${
                    active
                      ? "border-primary-600 ring-2 ring-primary-100"
                      : "border-line-input hover:border-primary-600"
                  }`}
                >
                  <span
                    className={`mx-auto grid h-6 w-6 place-items-center ${
                      active ? "text-primary-600" : "text-line-bold"
                    }`}
                  >
                    {option.shape === "number" ? (
                      <span className="text-[15px] font-bold">7</span>
                    ) : (
                      <RatingIcon shape={option.shape} className="h-5 w-5" />
                    )}
                  </span>
                  <span className="mt-1.5 block text-[11px] text-faint">
                    {option.label}
                  </span>
                </button>
              );
            })}
          </div>

          <label className={fieldCls}>
            <span className={labelCls}>Highest rating</span>
            <input
              type="number"
              min={3}
              max={10}
              className={inputCls}
              value={field.max}
              onChange={(e) => patch({ max: Number(e.target.value) } as Partial<FormField>)} // prettier-ignore
            />
          </label>
        </>
      ) : null}

      {/* ── Optional per-type settings ───────────────────────────────────
          Each is a toggle that reveals its input, rather than an always-on
          box: an empty "Minimum" field is ambiguous (no minimum, or a
          minimum you have not typed yet?), while a switch says plainly
          whether the rule is on. Switching off clears the value, so the
          document never carries a constraint the panel is not showing. */}

      {field.type === "short_text" || field.type === "long_text" ? (
        <>
          <h3 className={groupCls}>Answer rules</h3>
          <ToggleSetting
            title="Placeholder"
            hint="Grey hint text inside the box"
            on={field.placeholder !== undefined}
            onFlip={(on) => patch({ placeholder: on ? "" : undefined } as Partial<FormField>)} // prettier-ignore
          >
            <input
              className={inputCls}
              maxLength={100}
              value={field.placeholder ?? ""}
              placeholder="e.g. Tell us what happened"
              onChange={(e) => patch({ placeholder: e.target.value } as Partial<FormField>)} // prettier-ignore
            />
          </ToggleSetting>

          <ToggleSetting
            title="Minimum characters"
            hint="Refuse answers shorter than this"
            on={field.minLength !== undefined}
            onFlip={(on) => patch({ minLength: on ? 10 : undefined } as Partial<FormField>)} // prettier-ignore
          >
            <input
              type="number"
              min={1}
              max={field.maxLength}
              className={inputCls}
              value={field.minLength ?? ""}
              onChange={(e) => patch({ minLength: numberOrUndefined(e.target.value) } as Partial<FormField>)} // prettier-ignore
            />
          </ToggleSetting>

          <ToggleSetting
            title="Maximum characters"
            hint={`Currently ${field.maxLength}`}
            on
            locked
          >
            <input
              type="number"
              min={1}
              max={field.type === "long_text" ? 5000 : 500}
              className={inputCls}
              value={field.maxLength}
              onChange={(e) => patch({ maxLength: Number(e.target.value) || 1 } as Partial<FormField>)} // prettier-ignore
            />
          </ToggleSetting>

          {field.type === "long_text" ? (
            <label className={`${fieldCls} mt-4`}>
              <span className={labelCls}>Box height (rows)</span>
              <input
                type="number"
                min={2}
                max={12}
                className={inputCls}
                value={field.rows}
                onChange={(e) => patch({ rows: Number(e.target.value) || 4 } as Partial<FormField>)} // prettier-ignore
              />
            </label>
          ) : null}
        </>
      ) : null}

      {field.type === "email" || field.type === "dropdown" ? (
        <>
          <h3 className={groupCls}>Answer rules</h3>
          <ToggleSetting
            title="Placeholder"
            hint={
              field.type === "dropdown"
                ? "Text shown before a choice is made"
                : "Grey hint text inside the box"
            }
            on={field.placeholder !== undefined}
            onFlip={(on) => patch({ placeholder: on ? "" : undefined } as Partial<FormField>)} // prettier-ignore
          >
            <input
              className={inputCls}
              maxLength={100}
              value={field.placeholder ?? ""}
              placeholder={field.type === "email" ? "you@work.com" : "Select…"}
              onChange={(e) => patch({ placeholder: e.target.value } as Partial<FormField>)} // prettier-ignore
            />
          </ToggleSetting>
        </>
      ) : null}

      {field.type === "checkboxes" ? (
        <>
          <h3 className={groupCls}>How many can be picked</h3>
          <ToggleSetting
            title="At least"
            hint="Fewest boxes they must tick"
            on={field.minSelected !== undefined}
            onFlip={(on) => patch({ minSelected: on ? 1 : undefined } as Partial<FormField>)} // prettier-ignore
          >
            <input
              type="number"
              min={0}
              max={field.choices.length}
              className={inputCls}
              value={field.minSelected ?? ""}
              onChange={(e) => patch({ minSelected: numberOrUndefined(e.target.value) } as Partial<FormField>)} // prettier-ignore
            />
          </ToggleSetting>
          <ToggleSetting
            title="At most"
            hint="Most boxes they may tick"
            on={field.maxSelected !== undefined}
            onFlip={(on) => patch({ maxSelected: on ? field.choices.length : undefined } as Partial<FormField>)} // prettier-ignore
          >
            <input
              type="number"
              min={1}
              max={field.choices.length}
              className={inputCls}
              value={field.maxSelected ?? ""}
              onChange={(e) => patch({ maxSelected: numberOrUndefined(e.target.value) } as Partial<FormField>)} // prettier-ignore
            />
          </ToggleSetting>
        </>
      ) : null}

      {field.type === "number" ? (
        <>
          <h3 className={groupCls}>Answer rules</h3>
          <ToggleSetting
            title="Minimum"
            hint="Refuse anything lower"
            on={field.min !== undefined}
            onFlip={(on) => patch({ min: on ? 0 : undefined } as Partial<FormField>)} // prettier-ignore
          >
            <input
              type="number"
              className={inputCls}
              value={field.min ?? ""}
              onChange={(e) => patch({ min: numberOrUndefined(e.target.value) } as Partial<FormField>)} // prettier-ignore
            />
          </ToggleSetting>
          <ToggleSetting
            title="Maximum"
            hint="Refuse anything higher"
            on={field.max !== undefined}
            onFlip={(on) => patch({ max: on ? 100 : undefined } as Partial<FormField>)} // prettier-ignore
          >
            <input
              type="number"
              className={inputCls}
              value={field.max ?? ""}
              onChange={(e) => patch({ max: numberOrUndefined(e.target.value) } as Partial<FormField>)} // prettier-ignore
            />
          </ToggleSetting>
          <ToggleSetting
            title="Decimal places"
            hint={field.decimals === 0 ? "Whole numbers only" : `${field.decimals} allowed`} // prettier-ignore
            on
            locked
          >
            <input
              type="number"
              min={0}
              max={6}
              className={inputCls}
              value={field.decimals}
              onChange={(e) => patch({ decimals: Number(e.target.value) || 0 } as Partial<FormField>)} // prettier-ignore
            />
          </ToggleSetting>

          <div className="mt-4 grid grid-cols-2 gap-3">
            <label className={fieldCls}>
              <span className={labelCls}>Prefix</span>
              <input
                className={inputCls}
                maxLength={8}
                placeholder="$"
                value={field.prefix ?? ""}
                onChange={(e) => patch({ prefix: e.target.value || undefined } as Partial<FormField>)} // prettier-ignore
              />
            </label>
            <label className={fieldCls}>
              <span className={labelCls}>Suffix</span>
              <input
                className={inputCls}
                maxLength={8}
                placeholder="kg"
                value={field.suffix ?? ""}
                onChange={(e) => patch({ suffix: e.target.value || undefined } as Partial<FormField>)} // prettier-ignore
              />
            </label>
          </div>
        </>
      ) : null}

      {field.type === "date" ? (
        <>
          <h3 className={groupCls}>Answer rules</h3>
          <label className={fieldCls}>
            <span className={labelCls}>Ask for</span>
            <select
              className={inputCls}
              value={field.mode}
              onChange={(e) => patch({ mode: e.target.value as "date" | "datetime" } as Partial<FormField>)} // prettier-ignore
            >
              <option value="date">A date</option>
              <option value="datetime">A date and time</option>
            </select>
          </label>
          <ToggleSetting
            title="Earliest allowed"
            hint="Nothing before this date"
            on={field.min !== undefined}
            onFlip={(on) => patch({ min: on ? new Date().toISOString().slice(0, 10) : undefined } as Partial<FormField>)} // prettier-ignore
          >
            <input
              type="date"
              className={inputCls}
              value={field.min ?? ""}
              onChange={(e) => patch({ min: e.target.value || undefined } as Partial<FormField>)} // prettier-ignore
            />
          </ToggleSetting>
          <ToggleSetting
            title="Latest allowed"
            hint="Nothing after this date"
            on={field.max !== undefined}
            onFlip={(on) => patch({ max: on ? new Date().toISOString().slice(0, 10) : undefined } as Partial<FormField>)} // prettier-ignore
          >
            <input
              type="date"
              className={inputCls}
              value={field.max ?? ""}
              onChange={(e) => patch({ max: e.target.value || undefined } as Partial<FormField>)} // prettier-ignore
            />
          </ToggleSetting>
        </>
      ) : null}

      {field.type === "heading" ? (
        <label className={fieldCls}>
          <span className={labelCls}>Size</span>
          <select
            className={inputCls}
            value={field.level}
            onChange={(e) => patch({ level: Number(e.target.value) as 1 | 2 | 3 } as Partial<FormField>)} // prettier-ignore
          >
            <option value={1}>Large</option>
            <option value={2}>Medium</option>
            <option value={3}>Small</option>
          </select>
        </label>
      ) : null}

      {field.type === "file_upload" ? (
        <>
          <h3 className={groupCls}>Uploads</h3>
          <div className="grid grid-cols-2 gap-3">
            <label className={fieldCls}>
              <span className={labelCls}>Max files</span>
              <input
                type="number"
                min={1}
                max={10}
                className={inputCls}
                value={field.maxFiles}
                onChange={(e) => patch({ maxFiles: Number(e.target.value) || 1 } as Partial<FormField>)} // prettier-ignore
              />
            </label>
            <label className={fieldCls}>
              <span className={labelCls}>Max size (MB)</span>
              <input
                type="number"
                min={1}
                max={20}
                className={inputCls}
                value={field.maxSizeMb}
                onChange={(e) => patch({ maxSizeMb: Number(e.target.value) || 1 } as Partial<FormField>)} // prettier-ignore
              />
            </label>
          </div>

          <span className={labelCls}>Accepted files</span>
          <div className="flex flex-wrap gap-1.5">
            {FILE_ACCEPT_GROUPS.map((group) => {
              const on = field.accept.includes(group);
              return (
                <button
                  key={group}
                  type="button"
                  onClick={() =>
                    patch({
                      accept: on
                        ? field.accept.filter((a) => a !== group)
                        : [...field.accept, group],
                    } as Partial<FormField>)
                  }
                  className={`rounded-full border px-3 py-1.5 text-[12.5px] font-semibold capitalize ${
                    on
                      ? "border-primary-600 bg-primary-50 text-primary-600"
                      : "border-line-input text-muted hover:border-primary-600"
                  }`}
                >
                  {group}
                </button>
              );
            })}
          </div>
          <p className="mt-1.5 text-[12.5px] text-faint">
            Nothing selected means any file type.
          </p>
        </>
      ) : null}
    </div>
  );
};
